// ./src/utils/api/PolicyService.ts
//
// Policy (account-level reusable waivers / consents / house-rules) service.
// OpResult-native. Account-owned: every read is scoped to the caller's account
// and only the caller's own policies are editable (see the `rights` field).
// A multi-id update/remove is a bulk op (server restricts a bulk update to
// status). See em-scaffold-ui.
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";
import { Pagination, SortItem } from "./commonApiTypes";

// =============================================================================
// Constants (mirror the eventsmagic-api POLICY_CONTENT_FORMAT boundary)
// =============================================================================

// richtext = Tiptap WYSIWYG (stores HTML); html = hand-written raw HTML.
export const POLICY_CONTENT_FORMAT_VALUES = ["richtext", "markdown", "html", "plain"] as const;
export type PolicyContentFormat = (typeof POLICY_CONTENT_FORMAT_VALUES)[number];

/**
 * #164 — how a guest accepts a policy WITH a text (mirrors the server's `POLICY_ACCEPTANCE_METHOD`): 'sign' = each adult
 * (a minor's guardian) is emailed a link to accept it on its own page; 'checkbox' = ticked on the registration form, no
 * email, no date of birth unless age-limited. A policy without a text is always a tick box.
 */
export const POLICY_ACCEPTANCE_METHOD = { SIGN: "sign", CHECKBOX: "checkbox" } as const;
export type PolicyAcceptanceMethod = (typeof POLICY_ACCEPTANCE_METHOD)[keyof typeof POLICY_ACCEPTANCE_METHOD];

// Sanity bounds for the age range (mirror POLICY_AGE_MIN / POLICY_AGE_MAX).
export const POLICY_AGE_MIN = 0;
export const POLICY_AGE_MAX = 130;

/**
 * Bounds + default for an EVENT's age of majority (`event.ageOfMajority`) — the age at which a
 * guest signs a waiver themselves rather than a parent or guardian signing for them.
 *
 * ⚠ Mirrors the server's `rulesOptionalInt('ageOfMajority', { min: 10, max: 25 })` and the column's
 * `DEFAULT 18`. The real range worldwide runs 16 (Scotland) to 21 (Mississippi, Singapore, UAE);
 * 10–25 accepts every jurisdiction while still catching a typo'd 180.
 */
export const POLICY_AGE_OF_MAJORITY_MIN = 10;
export const POLICY_AGE_OF_MAJORITY_MAX = 25;
export const DEFAULT_AGE_OF_MAJORITY = 18;

// =============================================================================
// GraphQL
// =============================================================================

const POLICY_FIELDS = `
  id
  accountId
  name
  description
  label
  policyText
  contentFormat
  minAge
  maxAge
  requireInitials
  requireSignature
  acceptanceMethod
  status
  rights
  createdAt
  updatedAt
`;

const POLICY_LIST_QUERY = gql`
  query PolicyList($filter: PolicyFilter, $params: PaginationAndSorting) {
    policyList(filter: $filter, params: $params) {
      code
      data { ${POLICY_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const POLICY_GET_QUERY = gql`
  query PolicyGet($id: ID) {
    policyGet(id: $id) {
      code
      data { ${POLICY_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const POLICY_GET_MANY_QUERY = gql`
  query PolicyGetMany($ids: [ID]) {
    policyGetMany(ids: $ids) {
      code
      data { ${POLICY_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const POLICY_CREATE_MUTATION = gql`
  mutation PolicyCreate($params: PolicyInput) {
    policyCreate(params: $params) {
      code
      data { ${POLICY_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const POLICY_UPDATE_MUTATION = gql`
  mutation PolicyUpdate($where: PolicyWhereInput, $params: PolicyInput) {
    policyUpdate(where: $where, params: $params) {
      code
      data { ${POLICY_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const POLICY_REMOVE_MUTATION = gql`
  mutation PolicyRemove($where: PolicyWhereInput) {
    policyRemove(where: $where) {
      code
      data { id }
      errors { name errors warnings }
    }
  }
`;

// =============================================================================
// Types (match the eventsmagic-api Policy schema)
// =============================================================================

export interface Policy {
  id?: string;
  accountId?: string | null;
  /** Dialog title, shown to the guest. */
  name?: string | null;
  /** Internal note (not shown to guests). */
  description?: string | null;
  /** Acceptance line, e.g. "I accept this policy…". */
  label?: string | null;
  /** Full policy body (html/markdown). Null → render as a simple checkbox. */
  policyText?: string | null;
  contentFormat?: string | null;
  /** Applies to guests ≥ minAge (null = no lower bound). */
  minAge?: number | null;
  /** Applies to guests ≤ maxAge (null = no upper bound); both null = all guests. */
  maxAge?: number | null;
  requireInitials?: boolean | null;
  requireSignature?: boolean | null;
  /** 'sign' | 'checkbox' — see `POLICY_ACCEPTANCE_METHOD`. */
  acceptanceMethod?: string | null;
  status?: number;
  /** Actions the current caller may perform ('edit' | 'remove' | 'duplicate'). Empty = read-only. */
  rights?: string[] | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface PolicyInput {
  name?: string | null;
  description?: string | null;
  label?: string | null;
  policyText?: string | null;
  contentFormat?: string | null;
  minAge?: number | null;
  maxAge?: number | null;
  requireInitials?: boolean | null;
  requireSignature?: boolean | null;
  acceptanceMethod?: PolicyAcceptanceMethod | null;
  status?: number | null;
  /**
   * Authorization context (NOT stored): the event Compliance page the caller is acting from. Lets crew
   * with event.settings on it create a policy under the event's account (then attach it). Omit on Library.
   */
  eventId?: string | null;
}

// A bulk update (>1 id) may only mass-change status (server-enforced).
export interface PolicyBulkInput {
  status?: number | null;
}

export interface PolicyFilter {
  id?: string[] | null;
  status?: number[] | null;
  searchKeyword?: string | null;
  /**
   * Authorization/scoping context (NOT a column): the event Compliance page the list is shown on. Scopes
   * the policy library to that event's account for authorized crew (event.settings), so a co_host can pick.
   */
  eventId?: string | null;
}

export interface PolicyWhereInput {
  /** One id, or many for a bulk update/remove. */
  id?: string | string[];
}

// =============================================================================
// Service
// =============================================================================

export class PolicyService {
  static async list(
    filter?: PolicyFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    return ApiService.executeQuery(POLICY_LIST_QUERY, {
      filter: filter || {},
      params: { pagination: pagination || undefined, sortBy: sortBy || undefined },
    });
  }

  static async get(id: string): Promise<OpResult> {
    return ApiService.executeQuery(POLICY_GET_QUERY, { id });
  }

  static async getMany(ids: string[]): Promise<OpResult> {
    return ApiService.executeQuery(POLICY_GET_MANY_QUERY, { ids });
  }

  static async create(params: PolicyInput): Promise<OpResult> {
    return ApiService.executeMutation(POLICY_CREATE_MUTATION, { params });
  }

  static async update(where: PolicyWhereInput, params: PolicyInput): Promise<OpResult> {
    return ApiService.executeMutation(POLICY_UPDATE_MUTATION, { where, params });
  }

  static async remove(id: string): Promise<OpResult> {
    return ApiService.executeMutation(POLICY_REMOVE_MUTATION, { where: { id } });
  }

  /** Mass-change status on every selected policy in one call (server restricts a multi-id update to status). */
  static async bulkUpdate(ids: string[], params: PolicyBulkInput): Promise<OpResult> {
    if (ids.length === 0) {
      return new OpResult();
    }

    return ApiService.executeMutation(POLICY_UPDATE_MUTATION, { where: { id: ids }, params });
  }

  /** Remove every selected policy in one call. */
  static async removeMany(ids: string[]): Promise<OpResult> {
    if (ids.length === 0) {
      return new OpResult();
    }

    return ApiService.executeMutation(POLICY_REMOVE_MUTATION, { where: { id: ids } });
  }
}
